import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Leaf } from "lucide-react";
import { EASY_ARRIVAL_MEALS } from "@/data/easyArrivalMeals";
import { fmtUSD } from "@/lib/api";

export default function EasyArrivalPicker() {
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();

    const proceed = () => {
        if (!selected) return;
        navigate(`/book/easy-arrival?meal=${selected}`);
    };

    return (
        <div data-testid="easy-arrival-picker" className="pt-28 lg:pt-32 pb-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <Link
                    to="/packages/easy-arrival"
                    data-testid="back-to-easy-arrival"
                    className="inline-flex items-center gap-2 text-sm text-forest-700/70 hover:text-forest-700 mb-8"
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    Back to Easy Arrival Dinner
                </Link>

                <header className="max-w-3xl mb-12">
                    <span className="overline">Easy Arrival · Choose your meal</span>
                    <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl text-forest-700 mt-5 leading-[1.05]">
                        Pick the meal for your <em className="italic">first night.</em>
                    </h1>
                    <p className="text-forest-700/70 leading-relaxed mt-6 max-w-2xl">
                        Each Easy Arrival dinner is a flat {fmtUSD(350)} and typically
                        feeds 6 to 8. Choose one of the nine menus below — we'll
                        prepare it fresh and deliver it ready to enjoy.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {EASY_ARRIVAL_MEALS.map((m, i) => {
                        const isSelected = selected === m.id;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => setSelected(m.id)}
                                data-testid={`meal-card-${m.id}`}
                                className={`text-left bg-white border rounded-sm p-6 transition-all duration-300 animate-fade-up hover:-translate-y-1 ${
                                    isSelected
                                        ? "border-forest-700 ring-2 ring-forest-700/30 shadow-lg"
                                        : "border-palm-400/30 hover:border-forest-700/50"
                                }`}
                                style={{ animationDelay: `${i * 60}ms` }}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <Leaf
                                            className="w-4 h-4 text-forest-400 shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <span className="overline">{m.protein}</span>
                                    </div>
                                    <div
                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                            isSelected
                                                ? "border-forest-700 bg-forest-700 text-sand-100"
                                                : "border-palm-400/50"
                                        }`}
                                    >
                                        {isSelected && (
                                            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                                        )}
                                    </div>
                                </div>
                                <h3 className="font-serif text-2xl text-forest-700 leading-tight mt-3">
                                    {m.name}
                                </h3>
                                {m.tags.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {m.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="text-[10px] tracking-wider uppercase bg-forest-400/15 text-forest-700 px-2 py-0.5 rounded-sm"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <ul className="mt-4 space-y-2">
                                    {m.items.map((it, idx) => (
                                        <li
                                            key={idx}
                                            className="text-xs text-forest-700/75 leading-relaxed flex items-start gap-2"
                                        >
                                            <span className="text-forest-400 mt-0.5">~</span>
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </button>
                        );
                    })}
                </div>

                {/* Sticky bottom bar with the proceed button */}
                <div
                    className="sticky bottom-6 mt-12 z-30 flex justify-center"
                    data-testid="easy-arrival-cta"
                >
                    <div className="bg-forest-700 text-sand-100 rounded-sm shadow-xl shadow-forest-900/20 px-6 py-4 flex items-center gap-6 max-w-lg w-full mx-4">
                        <div className="flex-1 min-w-0">
                            <div className="overline !text-palm-400">Your meal</div>
                            <div
                                className="font-serif text-lg leading-tight truncate"
                                data-testid="selected-meal-name"
                            >
                                {selected
                                    ? EASY_ARRIVAL_MEALS.find((m) => m.id === selected)?.name
                                    : "Select one to continue"}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={proceed}
                            disabled={!selected}
                            data-testid="proceed-to-book-btn"
                            className="inline-flex items-center gap-2 bg-sand-100 text-forest-900 px-5 py-3 rounded-sm hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm tracking-wide whitespace-nowrap"
                        >
                            Continue to reserve
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
